import React from 'react';
import { Link } from 'react-router-dom';
import { Match, MatchPair } from '../api/matches';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface Props {
  match: Match;
}

function PlayerName({ player }: { player?: { id: string; first_name: string; last_name: string } | null }) {
  if (!player) return <span className="text-gray-400 italic">TBD</span>;
  return (
    <Link to={`/users/${player.id}`} className="font-medium hover:text-primary-600">
      {player.first_name} {player.last_name}
    </Link>
  );
}

function PairName({ pair }: { pair?: MatchPair | null }) {
  if (!pair) return <span className="text-gray-400 italic">TBD</span>;
  const p1 = pair.player1;
  const p2 = pair.player2;
  return (
    <span className="font-medium text-gray-900">
      {p1 ? <Link to={`/users/${p1.id}`} className="hover:text-primary-600">{p1.first_name} {p1.last_name}</Link> : '?'}
      {' / '}
      {p2 ? <Link to={`/users/${p2.id}`} className="hover:text-primary-600">{p2.first_name} {p2.last_name}</Link> : '?'}
    </span>
  );
}

function WinnerPairName({ pair }: { pair?: MatchPair | null }) {
  if (!pair) return null;
  const p1 = pair.player1;
  const p2 = pair.player2;
  return (
    <span>
      {p1 ? <Link to={`/users/${p1.id}`} className="hover:text-primary-600">{p1.first_name} {p1.last_name}</Link> : '?'}
      {' / '}
      {p2 ? <Link to={`/users/${p2.id}`} className="hover:text-primary-600">{p2.first_name} {p2.last_name}</Link> : '?'}
    </span>
  );
}

export default function MatchCard({ match }: Props) {
  const { user } = useAuth();
  const { t } = useTranslation();

  const statusLabel = t(`matchStatus.${match.status}`, { defaultValue: match.status });
  const statusClass = {
    pending: 'bg-gray-100 text-gray-600',
    result_submitted: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-600',
  }[match.status] ?? 'bg-gray-100 text-gray-600';

  const phaseLabel = t(`matchPhase.${match.phase}`, { defaultValue: match.phase });
  const isPairMatch = !!(match.home_pair_id || match.away_pair_id);

  const uid = user?.id;
  let isParticipant = false;
  if (uid) {
    if (!isPairMatch) {
      isParticipant = uid === match.home_player_id || uid === match.away_player_id;
    } else {
      const inHome = match.home_pair && (uid === match.home_pair.player1?.id || uid === match.home_pair.player2?.id);
      const inAway = match.away_pair && (uid === match.away_pair.player1?.id || uid === match.away_pair.player2?.id);
      isParticipant = !!(inHome || inAway);
    }
  }

  const canSubmit = isParticipant && match.status === 'pending' && !match.is_bye;
  const canConfirm = isParticipant && match.status === 'result_submitted' &&
    match.result?.submitter?.id !== uid;

  const actionLabel = canConfirm
    ? t('matchCard.confirmResult')
    : canSubmit
    ? t('matchCard.enterResult')
    : t('matchCard.details');

  return (
    <div className={`card hover:shadow-md transition-shadow ${match.is_bye ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{phaseLabel}</span>
          {match.round && <span className="text-xs text-gray-300">· R{match.round}</span>}
        </div>
        <span className={`badge ${statusClass}`}>{statusLabel}</span>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1 text-right">
          {isPairMatch ? <PairName pair={match.home_pair} /> : <PlayerName player={match.home_player} />}
        </div>

        <div className="flex-shrink-0 text-center">
          {match.result && match.result.status === 'confirmed' ? (
            <span className="font-bold text-lg text-gray-900">
              {match.result.home_sets_won} : {match.result.away_sets_won}
            </span>
          ) : (
            <span className="text-gray-300 font-bold text-lg">vs</span>
          )}
        </div>

        <div className="flex-1">
          {isPairMatch ? <PairName pair={match.away_pair} /> : <PlayerName player={match.away_player} />}
        </div>
      </div>

      {match.result && match.result.sets && Array.isArray(match.result.sets) && (
        <div className="mt-2 text-center">
          <span className="text-xs text-gray-400">
            {(match.result.sets as { home: number; away: number }[])
              .map((s) => `${s.home}:${s.away}`)
              .join(', ')}
          </span>
        </div>
      )}

      {match.status === 'confirmed' && (
        <div className="mt-2 text-center">
          <span className="text-xs text-primary-600 font-medium">
            {t('matchCard.winner')}:{' '}
            {isPairMatch
              ? <WinnerPairName pair={match.winner_pair} />
              : match.winner ? `${match.winner.first_name} ${match.winner.last_name}` : '–'}
          </span>
        </div>
      )}

      {match.result?.admin_edited_by && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <span>
            {t('matchCard.editedLater')}
            {match.result.admin_editor && (
              <> {t('matchCard.editedBy')} {match.result.admin_editor.first_name} {match.result.admin_editor.last_name}</>
            )}
          </span>
        </div>
      )}

      {!match.is_bye && (
        <div className="mt-4 flex justify-end">
          <Link
            to={`/matches/${match.id}`}
            className={`btn btn-sm ${canConfirm ? 'btn-primary' : canSubmit ? 'btn-primary' : 'btn-secondary'}`}
          >
            {actionLabel}
          </Link>
        </div>
      )}
    </div>
  );
}
